import { useState, useCallback, useEffect } from "react";
import {
  Grid,
  GridColumn,
  GridItemChangeEvent,
  GridCellProps,
  GridSelectionChangeEvent,
  GridHeaderSelectionChangeEvent,
  getSelectedState,
  GridPageChangeEvent,
  GridRowProps,
  GridFooterCellProps,
  GridSortChangeEvent,
  GridExpandChangeEvent,
  GridDetailRowProps,
  GridFilterChangeEvent,
  GridHeaderCellProps,
  GridToolbar,
  GridRowClickEvent,
  GridDataStateChangeEvent,
  GridGroupChangeEvent,
  GridFilterCell,
  GridColumnMenuFilter,
  GridNoRecords,
  GridPagerSettings,
  GRID_COL_INDEX_ATTRIBUTE,
} from "@progress/kendo-react-grid";
import { Pager, PageChangeEvent, useTableKeyboardNavigation } from "@progress/kendo-react-data-tools";
import {
  orderBy,
  SortDescriptor,
  filterBy,
  CompositeFilterDescriptor,
  GroupDescriptor,
} from "@progress/kendo-data-query";
import { getter } from "@progress/kendo-react-common";
import { CellRender, RowRender } from "./gridrenderers";
import { DropdownFilterCell } from "./dropDownFilterCell";
import { IGridProps } from "./IGridProps";
import * as React from "react";
import { IPageState } from "./IPageState";
import { GridSelectionType } from "./SelectionType";
import { FontIcon, IconButton, PrimaryButton } from "@fluentui/react";
import { _Styles } from "../../../content/styles/Page.styles";
import { IKendoGridColumnProps } from "./IKendoGridColumnProps";
import { ApplicationConstants } from "../../../models/ApplicationConstants";
import { MenuItems } from "../verticalmenu/menuItems";
import { JavaScriptExtensions } from "../../../infrastructure/JavaScriptExtensions";
import { FilterMenu } from "./gridfilter";
import { CssClassConstants } from "../../../content/CssClassConstants";
import { UIControlsText } from "../../../content/UIControlsText";
import {
  addExportExcelHeader,
  getUserRole,
  isNullOrEmpty,
  gridAccessibilityFix,
  applyDescriptiveGridCheckboxLabels,
} from "../common/util";
import {
  ExcelExport,
  ExcelExportColumn,
  ExcelExportColumnGroup,
} from "@progress/kendo-react-excel-export";
import moment from "moment";
import { GridType } from "../../../models/GridType";
import NextGenKendoChildGrid from "./nextgenkendochildgrid";
import { IGridChildProps } from "./IGridChildProps";
import { DealType } from "../../../models/DealType";
import { MessageTypeText } from "../../../models/MessageTypeText";
import {
  getRuleValidationMessage,
  getEntityDataFromSession,
} from "../../../components/shared/common/util";
import { SessionStorageConsts } from "../../../models/SessionStorageConsts";
import {
  useToAddRoleToGridColumnFilterButton,
  useToEnsureTabIndexSetForGridFilterButton,
  useToFixGridInvalidChildRoles,
  useToHandleOpenColumnMenuOnKeyboardEnter,
  useToFixFilterMenuComboboxAccessibility,
  useToAddAriaLabelToHierarchyHeaderButton,
  announceToScreenReader,
  useScreenReaderAnnouncer,
  useToRemoveGridHeaderRovingTabStop,
  useToEnsurePagerPageSizeAccessibleName,
} from "../../../hooks/accessiblity-hooks";
import { Guid } from "../../../infrastructure/Guid";
import { ActionType } from "../../../models/ActionType";

const sessionResourceData = getEntityDataFromSession<any>(
  SessionStorageConsts.resourceStrings,
);
/**
 * NextGenKendoGrid Component.
 * @function component
 */
const NextGenKendoGrid: React.FunctionComponent<IGridProps> = (
  props: IGridProps,
) => {
  const idGetter = getter(props.kendoGridProps.dataItemKey);
  const [selectedCheckState, setSelectedCheckState] = useState<{
    [id: string]: boolean | number[];
  }>({});
  const [detailSelectedCheckState, setDetailSelectedCheckState] = useState<{
    [id: string]: boolean | number[];
  }>({});
  const initialSort: Array<SortDescriptor> = [
    { field: props.kendoGridProps.initialSortField, dir: "asc" },
  ];
  const [sort, setSort] = useState(initialSort);
  const [editField, setEditField] = useState<string | undefined>(undefined);
  const [griddata, setGridData] = useState<any[]>([]);
  const [gridCount, setGridCount] = useState<number>(
    props.kendoGridProps.totalRecords,
  );
  const initialDataState: IPageState = {
    skip: 0,
    take:
      props.kendoGridProps.pageSizes?.[0] || props.kendoGridProps.noOfRecords,
  };
  const [page, setPage] = useState<IPageState>(initialDataState);
  const initialFilter: CompositeFilterDescriptor = {
    logic: "and",
    filters: [],
  };
  const [filter, setFilter] = useState(initialFilter);
  const [gridWidth, setGridWidth] = useState("auto");
  // MAS 1.4.10 (Reflow): at ~320 CSS px, Kendo's locked (frozen) columns render
  // as a separate table that overlaps the scrollable columns and truncates
  // content. Track reflow width so locked columns can be unlocked at that width,
  // turning the grid into a single horizontally-scrollable table (a permitted
  // two-dimensional-scroll data table).
  const [isReflow, setIsReflow] = useState<boolean>(
    typeof window !== "undefined" && !!window.matchMedia
      ? window.matchMedia("(max-width: 320px)").matches
      : false,
  );
  const [userRoles] = useState<string>(getUserRole());
  const [toggleText, setToggleExpandCollapse] = useState<string>(
    getRuleValidationMessage(
      sessionResourceData,
      MessageTypeText.FieldLabelText,
      "ExpandAll",
      UIControlsText.ExpandAll,
    ),
  );
  var date = moment().utcOffset("+05:30").format("MM-DD-YYYY");
  const gridContainerRef = React.useRef<HTMLDivElement>(null);
  const isLegalEntityGrid = props.kendoGridProps.gridName === GridType.LegalEntitySearch ||
    props.kendoGridProps.gridName === GridType.LegalEntitySelection;
  const legalEntityGridLabel = props.kendoGridProps.gridName === GridType.LegalEntitySearch
    ? getRuleValidationMessage(sessionResourceData, MessageTypeText.FieldLabelText, "LegalEntitiesSearchResult", UIControlsText.LegalEntitiesSearchResult)
    : getRuleValidationMessage(sessionResourceData, MessageTypeText.FieldLabelText, "SelectedLegalEntities", UIControlsText.SelectedLegalEntities);
  // NVDA table quick navigation does not reliably read aria-describedby; include the hint in the name.
  const legalEntityTableName = props.kendoGridProps.sortable
    ? `${legalEntityGridLabel}. ${getRuleValidationMessage(sessionResourceData, MessageTypeText.FieldLabelText, "GridSortableTableAnnouncement", UIControlsText.GridSortableTableAnnouncement)}`
    : legalEntityGridLabel;

  useEffect(() => {
    const container = gridContainerRef.current;
    if (!isLegalEntityGrid || !container) {
      return;
    }
    const setAttribute = (element: Element, name: string, value: string) => {
      if (element.getAttribute(name) !== value) {
        element.setAttribute(name, value);
      }
    };
    const updateTableStructure = () => {
      const grid = container.querySelector(":scope > .k-grid");
      if (!grid) {
        return;
      }
      setAttribute(grid, "aria-label", legalEntityTableName);
      const rows = Array.from(grid.querySelectorAll<HTMLTableRowElement>(
        ".k-grid-header thead > tr, .k-grid-content tbody > tr",
      )).filter(row => row.closest(".k-grid") === grid && !row.hidden &&
        getComputedStyle(row).display !== "none" && getComputedStyle(row).visibility !== "hidden");

      // These paginated tables expose the current page; the pager retains the full result count.
      setAttribute(grid, "aria-rowcount", `${rows.length}`);
      rows.forEach((row, index) => {
        setAttribute(row, "role", "row");
        setAttribute(row, "aria-rowindex", `${index + 1}`);
      });
    };
    updateTableStructure();
    const observer = new MutationObserver(updateTableStructure);
    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-rowcount", "aria-rowindex", "role"],
    });
    return () => observer.disconnect();
  }, [isLegalEntityGrid, legalEntityTableName]);

  useEffect(() => {
    props.resetPaginationToDefault && setPage(props.resetPaginationToDefault);
  }, [props.resetPaginationToDefault]);

  // MAS 1.4.10 (Reflow): keep isReflow in sync with the viewport so column
  // locking is disabled once the layout reflows to ~320 CSS px and re-enabled
  // when it widens again.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mediaQuery = window.matchMedia("(max-width: 320px)");
    const onChange = () => setIsReflow(mediaQuery.matches);
    onChange();
    mediaQuery.addEventListener("change", onChange);
    return () => mediaQuery.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    applyDescriptiveGridCheckboxLabels();
  });

  //listening the props change and reset griddata state
  useEffect(() => {
    if (props.kendoGridProps.defaultMainGridSelectionstate) {
      setSelectedCheckState(props.kendoGridProps.defaultMainGridSelectionstate);
    }
    SetGridWidth();
    if (!props.showFilterTextBox) {
      setFilter(initialFilter);
      setGridData(props.kendoGridProps.data);
    } else {
      setGridData(filterBy(props.kendoGridProps.data || [], filter));
    }
    setGridCount(
      isNullOrEmpty(props.kendoGridProps.totalRecords)
        ? props.kendoGridProps.data?.length
        : props.kendoGridProps.totalRecords,
    );
    window.addEventListener("resize", SetGridWidth);
    setToggleExpandCollapse(
      getRuleValidationMessage(
        sessionResourceData,
        MessageTypeText.FieldLabelText,
        "ExpandAll",
        UIControlsText.ExpandAll,
      ),
    );
  }, [
    props.kendoGridProps.data,
    props.kendoGridProps.defaultMainGridSelectionstate,
  ]);

  useEffect(() => {
    if (props.kendoGridProps.defaultDetailGridSelectionstate) {
      setDetailSelectedCheckState(
        props.kendoGridProps.defaultDetailGridSelectionstate,
      );
    }
  }, [props.kendoGridProps.defaultDetailGridSelectionstate]);

  //Fixing Accessibility for noRecords, sortIcons, GridPagerBtns & gridFootercell
  useEffect(() => {
    gridAccessibilityFix();
  });

  //customize the cell to render of selected legal grid
  const customCellRender: any = (
    td: React.ReactElement<HTMLTableCellElement>,
    cellProps: GridCellProps,
  ) => (
    <CellRender
      originalProps={cellProps}
      td={td}
      enterEdit={enterEditonSelectedData}
      editField={editField}
      keyboardEdit={props.kendoGridProps.gridName === GridType.LegalEntitySelection &&
        cellProps.field === ApplicationConstants.LegalEntitySelectionUnitstransact &&
        props.kendoGridProps.columns.some(col => col.apiField === cellProps.field && col.isEditable)}
      editLabel={props.kendoGridProps.columns.find(col => col.apiField === cellProps.field)?.columnTitle}
    />
  );
  //customize the row to render of selected legal grid
  const customRowRender: any = (
    tr: React.ReactElement<HTMLTableRowElement>,
    props: GridRowProps,
  ) => {
    return (
      <RowRender
        originalProps={props}
        tr={tr}
        exitEdit={exitEditonSelectedData}
        editField={editField}
      />
    );
  };
  //Editable cell creation on selecte Legals - on typing editable cell update value from user
  const onItemChange = (e: GridItemChangeEvent) => {
    let field = e.field || "";
    let value: any = e.value;
    let colProps = props.kendoGridProps.columns.filter(
      (col: IKendoGridColumnProps) => {
        if (col.apiField === field) return col.editorType;
      },
    )[0] as IKendoGridColumnProps;

    if (colProps) {
      if (colProps.editorType === ApplicationConstants.NumericEditorType) {
        if (e.value < 0) {
          return;
        } else {
          value = Math.floor(e.value);
        }
      }
    }
    e.dataItem[field] = value;
    let newData = (griddata || []).map((item: any) => {
      if (item.id === e.dataItem.id) {
        item[field] = value;
      }
      return item;
    });
    props.kendoGridProps.data = [...newData];
    setGridData(newData);
    // Note: onEditCallBack is called in exitEditonSelectedData, not here
    // Calling it here on every keystroke causes focus loss and multiple row creation
  };
  //--event for checkbox header selection in the search grid: when header checked all the child checkbox will be checked
  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      if (props.disableSelectAllCheckbox) {
        return;
      }
      const checkboxElement: any = event.syntheticEvent.target;
      const checked = checkboxElement.checked;
      const newSelectedState: any = {};
      event.dataItems.forEach((item) => {
        newSelectedState[idGetter(item)] = checked;
      });
      setSelectedCheckState(newSelectedState);
      props.onRowSelectionCallBack?.(newSelectedState);
    },
    [],
  );
  //event to trigger on change of checkbox in the grid
  const onSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      // event.dataItem is null when a drag-select is released outside any row; ignore it to
      // avoid "Cannot read properties of null (reading 'editMode')".
      if (!event.dataItem || event.dataItem.editMode) {
        return;
      }
      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedCheckState,
        dataItemKey: props.kendoGridProps.dataItemKey,
      });
      setSelectedCheckState(newSelectedState);
      //to enable check uncheck based on config, when multiple
      if (event.syntheticEvent !== undefined) {
        const checkboxElement: any = event.syntheticEvent.target;
        const checked = checkboxElement.checked;
        if (props.kendoGridProps.selectionType === GridSelectionType.Single) {
          event.dataItems.forEach((item) => {
            if (
              event.dataItem[props.kendoGridProps.dataItemKey] ===
              item[props.kendoGridProps.dataItemKey]
            ) {
              newSelectedState[idGetter(item)] = checked;
            } else {
              newSelectedState[idGetter(item)] = false;
            }
          });
          props.onPrimarySelectionCallBack?.(event.dataItem, checked);
        }
      }
      props.onRowSelectionCallBack?.(newSelectedState);
    },
    [selectedCheckState],
  );
  //--- Start of  hierarchal grid ---//
  const expandChange = (event: GridExpandChangeEvent) => {
    event.dataItem.expanded = event.value;
    setGridData([...(griddata || [])]);

    if (!event.value || event.dataItem.details) {
      return;
    }
    //populate data here on expand
    let data: any = (griddata || []).slice();
    let index = data.findIndex(
      (row: any) =>
        row[props.kendoGridProps.groupByColumn] ===
        event.dataItem[props.kendoGridProps.groupByColumn],
    );
    data[index].details = props.kendoGridProps.masterData?.filter(
      (item: any) =>
        item[props.kendoGridProps.groupByColumn] ===
        event.dataItem[props.kendoGridProps.groupByColumn],
    ); //sub grid value goes here
    setGridData(data);
  };

  const DetailComponent = useCallback(
    (childprops: GridDetailRowProps) => {
      var childData = childprops.dataItem.details
        ? childprops.dataItem.details
        : [];
      let childGridProps: IGridChildProps = {
        parent: props,
        childGridData: childData,
        defaultChildGridSelectionstate: detailSelectedCheckState,
      };
      return (
        <React.Fragment>
          <NextGenKendoChildGrid {...{ ...childGridProps }} />
        </React.Fragment>
      );
    },
    [detailSelectedCheckState],
  );
  //--- End of  hierarchal grid ---//
  //event to trigger on page change on pagination
  const pageChange = (event: GridPageChangeEvent) => {
    props.onPageChange?.(event);
    setPage(event.page);
    // Accessibility (WCAG 4.1.3): announce the new page so NVDA reports the pagination change.
    const take = event.page?.take || 0;
    const totalRecords = props.kendoGridProps.totalRecords || (griddata?.length ?? 0);
    if (take > 0) {
      const currentPage = Math.floor((event.page?.skip || 0) / take) + 1;
      const totalPages = Math.max(1, Math.ceil(totalRecords / take));
      announceToScreenReader(
        JavaScriptExtensions.stringFormat(
          getRuleValidationMessage(sessionResourceData, MessageTypeText.FieldLabelText, "GridPageChangedAnnouncement", UIControlsText.GridPageChangedAnnouncement),
          `${currentPage}`,
          `${totalPages}`
        ),
        "polite"
      );
    }
  };

  // The Kendo Pager is rendered as a sibling of the Grid (not inside role="grid") to satisfy
  // the aria-required-children accessibility rule (WCAG 1.3.1); a grid may only own rows/rowgroups.
  // Adapt the Pager's event to the GridPageChangeEvent shape the grid and consumers expect.
  const externalPagerChange = (event: PageChangeEvent) => {
    const gridEvent = {
      ...event,
      page: { skip: event.skip, take: event.take },
    } as unknown as GridPageChangeEvent;
    pageChange(gridEvent);
  };

  //--Start of Inline Edit Grid Functionality --//
  //on click of row enable editing
  const enterEditonSelectedData = (dataItem: any, field: string) => {
    if (field.match("unitsCommitted")) {
      const newData = (griddata || []).map((item: any) => ({
        ...item,
        inEdit: item.id === dataItem.id ? field : undefined,
      }));
      setGridData(newData);
      setEditField(field);
    } else if (dataItem.hasOwnProperty("isUploadedRecord")) {
      if (dataItem.isUploadedRecord) {
        const newData = (griddata || []).map((item: any) => ({
          ...item,
          inEdit: item.id === dataItem.id ? field : undefined,
        }));
        setGridData(newData);
        setEditField(field);
      }
    } else {
      const newData = (griddata || []).map((item: any) => ({
        ...item,
        inEdit: item.id === dataItem.id ? field : undefined,
      }));
      setGridData(newData);
      setEditField(field);
    }
  };

  const exitEditonSelectedData = () => {
    let editedItem = (griddata || []).filter(
      (item: any) => item.inEdit != undefined,
    );

    if (props.kendoGridProps.editField) {
      const newData = (griddata || []).map((item: any) => ({
        ...item,
        inEdit: undefined,
      }));
      setGridData(newData);
      setEditField(undefined);
    }
    props.onEditCallBack?.(editedItem[0]);
  };
  //--End of Inline Edit Grid Functionality --//

  //--Start of Delete Row in Grid Functionality --//
  const gridDeleteCell = (props: GridCellProps) => (
    <DeleteCell {...props} remove={removeSelectedRow} />
  );
  //method to render delete icon in grid component
  const DeleteCell = (props: GridCellProps & {
    remove: (dataItem: GridCellProps["dataItem"]) => void;
  }) => {
    const { dataItem } = props;
    const navigationAttributes = useTableKeyboardNavigation(props.id);
    const entityName = dataItem.legalEntityName?.trim();
    const deleteLabel = isLegalEntityGrid
      ? JavaScriptExtensions.stringFormat(
          entityName
            ? getRuleValidationMessage(sessionResourceData, MessageTypeText.FieldLabelText, "DeleteLegalEntityLabel", UIControlsText.DeleteLegalEntityLabel)
            : getRuleValidationMessage(sessionResourceData, MessageTypeText.FieldLabelText, "DeleteLegalEntityRowLabel", UIControlsText.DeleteLegalEntityRowLabel),
          entityName
            ? [entityName, dataItem.country].filter(Boolean).join(", ")
            : `${props.dataIndex - page.skip + customCellContext.current.headerRows + 1}`,
        )
      : CssClassConstants.DeleteIconName;
    return (
      <td
        role="gridcell"
        aria-colindex={props.ariaColumnIndex}
        aria-selected={props.isSelected}
        colSpan={props.colSpan}
        style={props.style}
        className={props.className}
        {...{ [GRID_COL_INDEX_ATTRIBUTE]: props.columnIndex }}
        {...navigationAttributes}
      >
        {!dataItem.editMode && <IconButton
          ariaLabel={deleteLabel}
          title={deleteLabel}
          iconProps={{ iconName: CssClassConstants.DeleteIconName, styles: { root: { color: "red" } } }}
          className={_Styles.DeleteRow}
          onClick={() => props.remove(dataItem)}
        />}
      </td>
    );
  };
  //callback method for removing selected row from grid on delete button click
  const removeSelectedRow = (deletedRowData: any) => {
    if (!deletedRowData.editMode) {
      const newList = (griddata || []).filter(
        (item) => item.id !== deletedRowData.id,
      );
      props.kendoGridProps.data = [...newList];
      setGridData([...newList]);
      // Accessibility (WCAG 4.1.3): announce the deleted record so NVDA reports the status change.
      const deletedName =
        deletedRowData?.legalEntityName ||
        deletedRowData?.name ||
        deletedRowData?.customerName ||
        deletedRowData?.resellerName;
      announceToScreenReader(
        deletedName
          ? `${deletedName} ${getRuleValidationMessage(sessionResourceData, MessageTypeText.FieldLabelText, "GridRecordDeletedAnnouncement", UIControlsText.GridRecordDeletedAnnouncement)}`
          : getRuleValidationMessage(sessionResourceData, MessageTypeText.FieldLabelText, "GridRecordDeletedAnnouncement", UIControlsText.GridRecordDeletedAnnouncement)
      );
      if (props.onDeleteCallBack) {
        props.onDeleteCallBack(deletedRowData, newList);
      }
    }
  };
  //--End of Delete Row in Grid Functionality --//
  // --Start of Download Row in Grid Functionality--//
  const gridDownloadCell = (props: GridCellProps) => (
    <DownloadCell {...props} />
  );
  const DownloadCell = (props: GridCellProps & { download?: any }) => {
    const { dataItem } = props;
    var gridContent = "data:application/pdf;base64," + dataItem.content;
    // MAS 2.1.1 (Keyboard): expose this custom Actions cell to the navigatable grid's roving tab
    // order so the download link inside it is reachable with the keyboard.
    const navigationAttributes = useTableKeyboardNavigation(props.id);
    return dataItem.editMode ? (
      <td className="k-command-cell" role="gridcell" aria-colindex={props.ariaColumnIndex} {...{ [GRID_COL_INDEX_ATTRIBUTE]: props.columnIndex }} {...navigationAttributes}></td>
    ) : (
      <td role="gridcell" aria-colindex={props.ariaColumnIndex} aria-selected={props.isSelected} {...{ [GRID_COL_INDEX_ATTRIBUTE]: props.columnIndex }} {...navigationAttributes}>
        <a download={dataItem.name} href={gridContent}>
          <FontIcon
            aria-label={CssClassConstants.DownloadIconName}
            iconName={CssClassConstants.DownloadIconName}
            className={_Styles.DeleteRow}
          ></FontIcon>
        </a>
      </td>
    );
  };

  // --End of Download Row in Grid Functionality--//

  const getColumnTotal = (field: string): number =>
    (griddata || []).reduce((total: number, item) => total + Number(item[field] || 0), 0);

  //to calculate total of column in footer cell
  const footerCellwithTotal = (footerprops: GridFooterCellProps) => {
    const field = footerprops.field || "";
    var findItem = props.kendoGridProps.columns.find(
      (x) => x.apiField === field,
    );
    var showFooter = false;
    if (findItem !== undefined) {
      showFooter = findItem.showFooter;
    }
    if (showFooter) {
      const total = getColumnTotal(field);
      return (
        <td colSpan={footerprops.colSpan} style={footerprops.style}>
          {" "}
          {findItem?.footerTitle} {total}
        </td>
      );
    }
    return <td></td>;
  };

  const pagerSettings: GridPagerSettings = {
    buttonCount: 5,
    info: true,
    type: "numeric",
    pageSizes: props.kendoGridProps.pageSizes,
    previousNext: true,
  };
  const addNew = () => {
    const newDataItem = {
      id: Guid.newGuid(),
      customerID: Guid.newGuid(),
      inEdit: undefined,
      Discontinued: false,
      isUploadedRecord: true,
      customerType: 2,
      country: "", //Initialize text fields
      legalEntityName: "",
      tpid: 0,
      crmAccountId: "",
      segment: "",
      vertical: "",
      unitsCommitted: 0,
      isManaged: false,
      errors: "",
      action: ActionType.Add,
    };
    props.kendoGridProps.data = [newDataItem, ...griddata];
    setGridData([newDataItem, ...griddata]);
    // Accessibility (WCAG 4.1.3): announce the added record so NVDA reports the status change.
    announceToScreenReader(
      getRuleValidationMessage(sessionResourceData, MessageTypeText.FieldLabelText, "GridRecordAddedAnnouncement", UIControlsText.GridRecordAddedAnnouncement)
    );
  };

  const renderHeaderCell = (props: GridHeaderCellProps, isMandatory: boolean) => {
    const isSortable = typeof props.onClick === "function";
    const titleNode = isMandatory ? (
      <>
        {props.title} <span className={"gridmandatoryField"} aria-hidden="true">*</span>
      </>
    ) : (
      props.title
    );
    if (!isSortable) {
      return (
        <span className="k-link" title={props.title}>
          {titleNode}
        </span>
      );
    }
    const onHeaderKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        props.onClick(event);
      }
    };
    const headerAriaLabel = `${props.title}, sortable column`;
    return (
      <span
        className="k-link"
        title={props.title}
        role="button"
        tabIndex={0}
        aria-label={headerAriaLabel}
        onClick={props.onClick}
        onKeyDown={onHeaderKeyDown}
      >
        {titleNode}
        <span aria-hidden="true">{props.children}</span>
      </span>
    );
  };

  const mandatoryHeaderCell = (props: GridHeaderCellProps) =>
    renderHeaderCell(props, true);
  const HeaderCell = (props: GridHeaderCellProps) => renderHeaderCell(props, false);

  // WCAG 4.1.3 (Status Messages): When a sortable column header button is activated (mouse or
  // keyboard) the sort direction is not conveyed by focus alone, so announce the resulting state
  // to NVDA via the shared live region. Applies to every column header button in the grid.
  const getColumnTitleByField = (field?: string): string => {
    if (!field) {
      return "";
    }
    const match = props.kendoGridProps.columns?.find(
      (col) => col.apiField === field,
    );
    return match?.columnTitle ?? field;
  };
  const announceSortChange = (newSort: Array<SortDescriptor>) => {
    const previousState = new Set(
      (sort || []).map((descriptor) => `${descriptor.field}:${descriptor.dir}`),
    );
    // A newly added/changed descriptor represents the column the user just sorted.
    const changed = (newSort || []).find(
      (descriptor) =>
        !previousState.has(`${descriptor.field}:${descriptor.dir}`),
    );
    if (changed) {
      const directionText =
        changed.dir === "asc" ? "sorted ascending" : "sorted descending";
      announceToScreenReader(
        `${getColumnTitleByField(changed.field)}, ${directionText}`,
        "assertive",
      );
      return;
    }
    // No new descriptor => a previously sorted column was toggled back to unsorted.
    const removed = (sort || []).find(
      (descriptor) =>
        !(newSort || []).some((next) => next.field === descriptor.field),
    );
    if (removed) {
      announceToScreenReader(
        `${getColumnTitleByField(removed.field)}, not sorted`,
        "assertive",
      );
    }
  };

  const allowView = (config: any) => {
    return (
      (config?.allowColumnView?.length == 0
        ? true
        : config?.allowColumnView?.some((r: any) => userRoles.includes(r))
          ? true
          : false) &&
      (config.isVisible == null || config.isVisible ? true : false)
    );
  };
  const VerticalMenuCell = (menuprops: { dataItem?: any }) => {
    const { dataItem } = menuprops;
    return (
      <td>
        <MenuItems
          dashBoardConfig={props.kendoGridProps}
          dashBoardData={menuprops}
          selectedData={dataItem}
        ></MenuItems>
      </td>
    );
  };
  const gridErrorCell = (props: GridCellProps) => <ErrorCell {...props} />;

  const ErrorCell = (props: GridCellProps) => {
    const { dataItem } = props;
    const navigationAttributes = useTableKeyboardNavigation(props.id);
    return (
      <td
        role="gridcell"
        aria-colindex={props.ariaColumnIndex}
        aria-selected={props.isSelected}
        colSpan={props.colSpan}
        style={props.style}
        className={props.className}
        {...{ [GRID_COL_INDEX_ATTRIBUTE]: props.columnIndex }}
        {...navigationAttributes}
      >
        {!dataItem.editMode && <>
          <p className="error-msg"> {dataItem.errors}</p>
          <p className="warning-msg"> {dataItem.warnings}</p>
        </>}
      </td>
    );
  };

  //method to update grid data and count based on filter from context menu of grid column
  const filterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    // Flatten any nested composite filters so we can tell whether the user still has a non-empty
    // search value typed in. Kendo keeps firing onFilterChange while the text is deleted, and the
    // final keystroke leaves behind either an empty filters array or an empty-value descriptor.
    const flattenFilters = (filters: any[]): any[] =>
      (filters || []).reduce(
        (acc: any[], f: any) => acc.concat(Array.isArray(f?.filters) ? flattenFilters(f.filters) : f),
        []
      );
    const hasActiveFilter =
      !!e.filter &&
      flattenFilters(e.filter.filters).some(
        (f: any) => f && f.value !== "" && f.value !== null && f.value !== undefined
      );

    let displayedCount: number;
    if (hasActiveFilter) {
      const filteredData = filterBy(props.kendoGridProps.data || [], e.filter);
      displayedCount = filteredData.length;
      setGridCount(displayedCount);
      setGridData(filteredData);
    } else {
      // Filter fully cleared: restore the complete data set and its total count so the grid and the
      // announced result count stay in sync. Previously the empty filter was applied to the already
      // filtered griddata, so removing the search text left a stale subset (and count) in place,
      // which NVDA then announced as an incorrect record count.
      const fullData = props.kendoGridProps.data || [];
      displayedCount = (props.kendoGridProps.totalRecords ?? fullData.length) as number;
      setGridCount(displayedCount);
      setGridData(fullData);
    }

    // Accessibility (WCAG 4.1.3 Status Messages): announce the number of rows now shown so NVDA
    // reports the status change on every filter update - including when the search text is cleared,
    // where it announces the restored full count instead of a stale value.
    announceToScreenReader(
      displayedCount === 0
        ? getRuleValidationMessage(sessionResourceData, MessageTypeText.FieldLabelText, "GridSearchNoResultsFoundAnnouncement", UIControlsText.GridSearchNoResultsFoundAnnouncement)
        : displayedCount === 1
          ? getRuleValidationMessage(sessionResourceData, MessageTypeText.FieldLabelText, "GridSearchSingleResultFoundAnnouncement", UIControlsText.GridSearchSingleResultFoundAnnouncement)
          : JavaScriptExtensions.stringFormat(
              getRuleValidationMessage(sessionResourceData, MessageTypeText.FieldLabelText, "GridSearchResultsFoundAnnouncement", UIControlsText.GridSearchResultsFoundAnnouncement),
              `${displayedCount}`
            ),
      "polite"
    );
  };

  const clearGridData = () => {
    props.kendoGridProps.data = [];
    setGridData([]);
    if (props.onDeleteCallBack) {
      props.onDeleteCallBack({}, []);
    }
  };
  const SetGridWidth = () => {
    if (
      JavaScriptExtensions.isEmptyOrNullOrUndefined(props.kendoGridProps.width)
    ) {
      setGridWidth("auto");
    } else if (props.kendoGridProps.width === "0") {
      let headerElement = document.getElementsByClassName("headerStyle");
      let width =
        headerElement.length > 0 ? headerElement[0].clientWidth + "px" : "auto";
      setGridWidth(width);
    }
  };

  //method to download grid data in to excel file
  const _export = React.useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      if (props.kendoGridProps.gridName !== GridType.LegalEntityUpload) {
        addExportExcelHeader(_export, props);
      } else {
        _export.current.save();
      }
    }
  };

  const CategoryFilterCell = (filterProps: any) => (
    <DropdownFilterCell
      {...filterProps}
      data={props.dropDownFilterContent}
      defaultItem={filterProps.defaultItem}
    />
  );
  //Generates the grid columns for the childcolumns of a single column
  const getChildColumns = (kendoGridChildColumns: IKendoGridColumnProps[]) => {
    if (kendoGridChildColumns === undefined) {
      return [];
    }
    const tempKendoGridColumnsProps = kendoGridChildColumns.map((col) => {
      return (
        <GridColumn
          key={col.key}
          field={col.apiField}
          title={col.columnTitle}
          width={col.width}
          editable={col.isEditable}
          sortable={col.allowSorting}
          filterable={col.allowFilter}
        />
      );
    });
    return tempKendoGridColumnsProps;
  };
  const getColumns = (col: IKendoGridColumnProps, error: string) => {
    if (col === undefined) {
      return [];
    }
    if (col.childColumns && col.childColumns.length > 0) {
      return (
        <GridColumn
          title={col.columnTitle}
          children={getChildColumns(col.childColumns)}
          locked={isReflow ? false : col.locked}
        />
      );
    } else {
      if (error !== "errors") {
        return (
          <GridColumn
            key={col.key}
            field={col.apiField}
            title={col.columnTitle}
            width={col.width}
            editable={col.isEditable}
            sortable={col.allowSorting}
            filterable={col.allowFilter}
            editor={col.editorType}
            className={col.cssClass}
            footerCell={isLegalEntityGrid ? undefined : footerCellwithTotal}
            format={col.editorFormat}
            columnMenu={col.allowFilter ? FilterMenu : undefined}
            children={getChildColumns(col.childColumns)}
            headerClassName={
              isColumnActive(col.apiField) ? CssClassConstants.ActiveClass : ""
            }
            filterCell={
              col.categoryFilterCell ? CategoryFilterCell : GridFilterCell
            }
            headerCell={col.mandatoryField ? mandatoryHeaderCell : HeaderCell}
            cell={col.isCustomCell ? gridRenderCustomCell : undefined}
            locked={isReflow ? false : col.locked}
          />
        );
      } else {
        return (
          <GridColumn
            key={col.key}
            field={col.apiField}
            title={col.columnTitle}
            width={col.width}
            editable={col.isEditable}
            sortable={col.allowSorting}
            filterable={col.allowFilter}
            className={col.cssClass}
            cell={gridErrorCell}
            children={getChildColumns(col.childColumns)}
            filterCell={
              col.categoryFilterCell ? CategoryFilterCell : GridFilterCell
            }
            headerCell={col.mandatoryField ? mandatoryHeaderCell : HeaderCell}
            locked={isReflow ? false : col.locked}
          />
        );
      }
    }
  };

  //Toggle Grid Data (Expand All and Collapse All)
  const toggleExpand = () => {
    setToggleExpandCollapse(
      toggleText ===
        getRuleValidationMessage(
          sessionResourceData,
          MessageTypeText.FieldLabelText,
          "ExpandAll",
          UIControlsText.ExpandAll,
        )
        ? getRuleValidationMessage(
            sessionResourceData,
            MessageTypeText.FieldLabelText,
            "CollapseAll",
            UIControlsText.CollapseAll,
          )
        : getRuleValidationMessage(
            sessionResourceData,
            MessageTypeText.FieldLabelText,
            "ExpandAll",
            UIControlsText.ExpandAll,
          ),
    );
    let toggleData = (griddata || []).map((eachRowItem) => {
      eachRowItem.details = props.kendoGridProps.masterData?.filter(
        (eachRecord: any) =>
          eachRecord[props.kendoGridProps.groupByColumn] ===
          eachRowItem[props.kendoGridProps.groupByColumn],
      );
      if (isNullOrEmpty(eachRowItem.expanded)) {
        eachRowItem.expanded = true;
      } else {
        eachRowItem.expanded =
          toggleText ===
          getRuleValidationMessage(
            sessionResourceData,
            MessageTypeText.FieldLabelText,
            "ExpandAll",
            UIControlsText.ExpandAll,
          )
            ? true
            : false;
      }
      return eachRowItem;
    });
    setGridData(toggleData);
  };

  const isColumnActive = (field: string) => {
    return GridColumnMenuFilter.active(field, filter);
  };
  const customCellContext = React.useRef({
    render: props.onCustomCellRenderCallBack,
    isLegalEntityGrid,
    skip: page.skip,
    headerRows: props.showFilterTextBox ? 2 : 1,
  });
  customCellContext.current = {
    render: props.onCustomCellRenderCallBack,
    isLegalEntityGrid,
    skip: page.skip,
    headerRows: props.showFilterTextBox ? 2 : 1,
  };
  // Keep the cell component stable so committing an edit does not remount the next focused control.
  const gridRenderCustomCell = useCallback((selectedRowData: GridCellProps) => {
    const context = customCellContext.current;
    return context.render
      ? context.render({
          ...selectedRowData,
          ariaRowIndex: context.isLegalEntityGrid
            ? selectedRowData.dataIndex - context.skip + context.headerRows + 1
            : undefined,
        })
      : "";
  }, []);
  const addNewPanel = () => {
    if (props.onAddNewPanelCallBack) {
      props.onAddNewPanelCallBack();
    }
  };
  const getAddNewButtonText = () => {
    if (props.kendoGridProps.addNewButtonText) {
      return props.kendoGridProps.addNewButtonText;
    } else {
      return getRuleValidationMessage(
        sessionResourceData,
        MessageTypeText.FieldLabelText,
        "AddNew",
        UIControlsText.AddNew,
      );
    }
  };
  const customNoErrorMessage = () => {
    if (props.customNoRecordsErrorMessage !== undefined) {
      return props.customNoRecordsErrorMessage();
    } else if (props.kendoGridProps.noRecordsCustomMessage !== undefined) {
      return props.kendoGridProps.noRecordsCustomMessage;
    } else {
      return getRuleValidationMessage(
        sessionResourceData,
        MessageTypeText.OpportunityValidationError,
        "NoRecordsAvailableText",
        UIControlsText.NoRecordsAvailableText,
      );
    }
  };

  const handleDealTypeAbbreviation = (dealtype?: string) => {
    switch (dealtype) {
      case DealType.SpecialPricing:
        return "SP";
      case DealType.DealRegistration:
        return "DR";
      default:
        return "";
    }
  };

  const handleExportFileName = () => {
    var gridName = props.kendoGridProps.gridName;
    var fileName = gridName + " " + date;
    var dealtypeString = props.kendoGridProps.dealType;
    var dash = " - ";
    if (gridName === GridType.DRSPExpandView) {
      return (
        props.kendoGridProps.caseNumber +
        dash +
        handleDealTypeAbbreviation(dealtypeString) +
        dash +
        "Expanded View " +
        date
      );
    } else if (gridName === GridType.DRSPCompactView) {
      return (
        props.kendoGridProps.caseNumber +
        dash +
        handleDealTypeAbbreviation(dealtypeString) +
        dash +
        "Compact View " +
        date
      );
    } else if (gridName === GridType.BUView) {
      return (
        props.kendoGridProps.caseNumber +
        dash +
        handleDealTypeAbbreviation(dealtypeString) +
        dash +
        "BU View " +
        date
      );
    }

    return fileName;
  };

  useToAddRoleToGridColumnFilterButton();
  useToHandleOpenColumnMenuOnKeyboardEnter();
  useToFixFilterMenuComboboxAccessibility();
  useToFixGridInvalidChildRoles();
  useToAddAriaLabelToHierarchyHeaderButton();
  useScreenReaderAnnouncer();
  useToRemoveGridHeaderRovingTabStop();
  useToEnsurePagerPageSizeAccessibleName();

  return (
    <React.Fragment>
      <ExcelExport
        data={griddata}
        fileName={handleExportFileName()}
        ref={_export}
      >
        {props.kendoGridProps.columns.map((col) => {
          if (allowView(col)) {
            if (col.childColumns && col.childColumns.length > 1) {
              return (
                <ExcelExportColumnGroup
                  title={col.columnTitle}
                  width={parseInt(col.width)}
                  headerCellOptions={{ textAlign: "left" }}
                >
                  {col.childColumns.map((item) => {
                    return (
                      <ExcelExportColumn
                        field={item.apiField}
                        title={item.columnTitle}
                      />
                    );
                  })}
                </ExcelExportColumnGroup>
              );
            } else if (col.childColumns && col.childColumns.length === 1) {
              return (
                <ExcelExportColumn
                  field={col.apiField}
                  title={
                    col.columnTitle + " - " + col.childColumns[0].columnTitle
                  }
                  width={parseInt(col.width)}
                />
              );
            } else {
              return (
                <ExcelExportColumn
                  field={col.apiField}
                  title={col.columnTitle}
                  width={parseInt(col.width)}
                />
              );
            }
          }
        })}
      </ExcelExport>
      <div id="mdppAccessibilityNextGenGridContainer" ref={gridContainerRef}>
        {(props.kendoGridProps.actionTypes?.showAddNewRowGridAction ||
          props.kendoGridProps.actionTypes?.showClearAllGridAction ||
          props.kendoGridProps.actionTypes?.showDownloadExcelGridAction ||
          props.kendoGridProps.actionTypes?.showExpandCollapseGridAction ||
          props.kendoGridProps.actionTypes?.showAddNewPanelGridAction ||
          !isNullOrEmpty(props.kendoGridProps.gridDisplayName)) && (
          <GridToolbar>
            <div id="gridTitle">{props.kendoGridProps.gridDisplayName}</div>
            <div className="ms-auto">
              {props.kendoGridProps.actionTypes?.showAddNewRowGridAction && (
                <PrimaryButton
                  text={getRuleValidationMessage(
                    sessionResourceData,
                    MessageTypeText.FieldLabelText,
                    "AddNew",
                    UIControlsText.AddNew,
                  )}
                  onClick={addNew}
                  className="me-2"
                />
              )}
              {props.kendoGridProps.actionTypes?.showAddNewPanelGridAction && (
                <PrimaryButton
                  text={getAddNewButtonText()}
                  onClick={addNewPanel}
                  className="me-2"
                />
              )}
              {props.kendoGridProps.actionTypes?.showDownloadExcelGridAction &&
                props.kendoGridProps.data?.length > 0 && (
                  <PrimaryButton
                    text={getRuleValidationMessage(
                      sessionResourceData,
                      MessageTypeText.FieldLabelText,
                      "DownloadExcel",
                      UIControlsText.DownloadExcel,
                    )}
                    onClick={excelExport}
                    className="me-2"
                  />
                )}
              {props.kendoGridProps.actionTypes?.showClearAllGridAction &&
                props.kendoGridProps.data?.length > 0 && (
                  <PrimaryButton
                    text={getRuleValidationMessage(
                      sessionResourceData,
                      MessageTypeText.FieldLabelText,
                      "ClearAll",
                      UIControlsText.ClearAll,
                    )}
                    onClick={clearGridData}
                    className="me-2"
                  />
                )}
              {props.kendoGridProps.actionTypes?.showExpandCollapseGridAction &&
                props.kendoGridProps.data?.length > 0 && (
                  <PrimaryButton
                    text={toggleText}
                    onClick={toggleExpand}
                    className="me-2"
                  />
                )}
            </div>
          </GridToolbar>
        )}
        <Grid
          data={
            props.kendoGridProps.expandField !== ""
              ? orderBy(
                  (griddata || []).slice(page.skip, page.take + page.skip),
                  sort,
                )
              : props.kendoGridProps.isServerSideGrid
                ? orderBy(
                    (griddata || []).map((item) => ({
                      ...item,
                      [props.kendoGridProps.selectedField]:
                        selectedCheckState[idGetter(item)],
                    })),
                    sort,
                  )
                : orderBy(
                    (griddata || [])
                      .map((item) => ({
                        ...item,
                        [props.kendoGridProps.selectedField]:
                          selectedCheckState[idGetter(item)],
                      }))
                      .slice(page.skip, page.take + page.skip),
                    sort,
                  )
          }
          skip={page.skip}
          take={page.take}
          dataItemKey={props.kendoGridProps.dataItemKey}
          selectedField={props.kendoGridProps.selectedField}
          selectable={{
            enabled: false,
            drag: false,
            cell: false,
            mode:
              props.kendoGridProps.selectionType === GridSelectionType.Mulitple
                ? "multiple"
                : "single",
          }}
          resizable={props.kendoGridProps.resizable}
          sortable={props.kendoGridProps.sortable}
          sort={sort}
          // pageable={props.kendoGridProps.pageable}
          filter={filter}
          filterable={props.showFilterTextBox ? true : false}
          onFilterChange={filterChange}
          onItemChange={onItemChange}
          onSelectionChange={
            props.kendoGridProps.editMode ? undefined : onSelectionChange
          }
          onHeaderSelectionChange={
            props.kendoGridProps.selectionType === GridSelectionType.Mulitple
              ? onHeaderSelectionChange
              : undefined
          }
          onPageChange={pageChange}
          onSortChange={(e: GridSortChangeEvent) => {
            announceSortChange(e.sort);
            setSort(e.sort);
          }}
          total={gridCount}
          className={props.kendoGridProps.className}
          cellRender={customCellRender}
          rowRender={
            props.kendoGridProps.actionTypes?.showGridContextMenuAction
              ? null
              : customRowRender
          }
          editField={props.kendoGridProps.editField}
          onExpandChange={expandChange}
          expandField={props.kendoGridProps.expandField}
          detail={props.kendoGridProps.masterData ? DetailComponent : null}
          style={{ width: gridWidth }}
          navigatable={true}
          pageable={false}
        >
          {props.kendoGridProps.actionTypes?.showGridContextMenuAction && (
            <GridColumn
              cell={VerticalMenuCell}
              filterable={false}
              width="40px"
            />
          )}
          {props.kendoGridProps.actionTypes
            ?.showSelectAllCheckboxGridAction && (
            <GridColumn
              field={props.kendoGridProps.selectedField}
              filterable={false}
              headerSelectionValue={
                griddata?.findIndex(
                  (item: any) => !selectedCheckState[idGetter(item)],
                ) === -1
              }
            />
          )}
          {props.kendoGridProps.columns.map((col) => {
            if (allowView(col)) {
              return getColumns(col, col.key);
            }
          })}
          {props.kendoGridProps.additionalColumns?.map((col) => {
            if (col.isVisible) {
              return (
                <GridColumn
                  field={col.apiField}
                  key={col.key}
                  title={col.columnTitle}
                  headerCell={mandatoryHeaderCell}
                  filterable={col.allowFilter}
                  width={col.width}
                  className={col.cssClass}
                />
              );
            }
          })}
          {props.kendoGridProps.actionTypes?.showRowDeleteGridAction && (
            <GridColumn
              cell={gridDeleteCell}
              title="Actions"
              filterable={false}
            />
          )}
          {props.kendoGridProps.actionTypes?.showRowDownloadGridAction && (
            <GridColumn
              cell={gridDownloadCell}
              title="Actions"
              filterable={false}
            />
          )}
          {props.kendoGridProps.noRecordsCustomMessage && (
            <GridNoRecords>
              <div
                dangerouslySetInnerHTML={{ __html: customNoErrorMessage() }}
              ></div>
            </GridNoRecords>
          )}
        </Grid>
        {props.kendoGridProps.pageable && (
          <Pager
            skip={page.skip}
            take={page.take}
            total={gridCount}
            buttonCount={pagerSettings.buttonCount}
            info={pagerSettings.info}
            type={pagerSettings.type}
            pageSizes={
              Array.isArray(pagerSettings.pageSizes)
                ? pagerSettings.pageSizes
                : undefined
            }
            previousNext={pagerSettings.previousNext}
            onPageChange={externalPagerChange}
            className="k-grid-pager"
          />
        )}
        {isLegalEntityGrid && props.kendoGridProps.columns
          .filter(col => allowView(col) && col.showFooter)
          .map(col => (
            <div key={col.key} className="k-grid-footer" role="note">
              {col.footerTitle} {getColumnTotal(col.apiField)}
            </div>
          ))}
      </div>
    </React.Fragment>
  );
};
export default NextGenKendoGrid;
